/**
 * PSR-2 — Public sentiment coverage auto-fill.
 *
 * Maps the SED `publicSentiment` section (PSE-1 orchestrator output)
 * onto the `community-feedback-summary` checklist requirement's
 * `authoredSchema`. Mirrors the `floodCoverageFill` / `geotechCoverageFill`
 * CAR pattern: pure transform, no HTTP, no Gemini.
 *
 * Field map:
 *   sentimentScore_0to100 ← round((meanSentiment + 1) * 50) from the
 *                           `general-datacenter` perTech bucket
 *   overallStance         ← threshold bands on meanSentiment
 *   keyThemes             ← top theme names from `extractThemes()` or
 *                           taxonomy fallback when sample floor not met
 *
 * Intentionally omitted (doc-only — need a real stakeholder log):
 *   stakeholderCount, lastEngagementDate
 */
import type {
  PerTechSentiment,
  PublicSentimentEntry,
  PublicSentimentSection,
  SiteEvidenceDocument,
} from '../../types/siteEvidence';
import type { PsrPublicSentimentEntry } from '../../services/publicSentimentOrchestrator';
import {
  extractThemes,
  THEME_TAXONOMY,
  THEME_MAX_COUNT,
} from '../../services/sentiment/extractThemes';
import { inferAutoFillConfidence, type InferAutoFillConfidenceResult } from './confidenceTier';
import type { RequirementAutoFill } from './compileCoverageAutoFill';

/** Mirrors `community-feedback-summary` authoredSchema keys (excl. notes). */
export const COMMUNITY_FEEDBACK_FIELD_KEYS = [
  'overallStance',
  'sentimentScore_0to100',
  'keyThemes',
  'stakeholderCount',
  'lastEngagementDate',
] as const;

/** PSE-3 / narrativeHygiene oppositional floor — reused for stance bands. */
export const PSE_OPPOSITIONAL_THRESHOLD = -0.2;

/** Mean sentiment at or above this ⇒ `supportive`. */
export const PSE_SUPPORTIVE_THRESHOLD = 0.2;

/** |meanSentiment| within this band ⇒ `neutral`. */
export const PSE_NEUTRAL_BAND = 0.05;

/** Lower sample floor for coverage keyThemes when extractThemes is empty. */
export const COVERAGE_THEME_MIN_SAMPLE_COUNT = 2;

export type CommunityFeedbackStance = 'supportive' | 'mixed' | 'neutral' | 'oppositional';

export interface PublicSentimentCoverageFill {
  fields: Partial<Record<typeof COMMUNITY_FEEDBACK_FIELD_KEYS[number], string | number>>;
  notes: string;
  fieldsFilledCount: number;
  fieldsTotalCount: number;
  sourceUrl: string;
  sourceProvider: 'public-sentiment-orchestrator';
  confidence: InferAutoFillConfidenceResult;
}

const PUBLIC_SENTIMENT_SOURCE_URL = 'https://www.gdeltproject.org/';

/**
 * Map mean sentiment in [-1, +1] to the 0–100 index used by the
 * authored schema and PSR SENTIMENT INDEX vocabulary.
 */
export function meanSentimentToScore0to100(meanSentiment: number): number | null {
  if (!Number.isFinite(meanSentiment)) return null;
  return Math.round(Math.max(0, Math.min(100, (meanSentiment + 1) * 50)));
}

/**
 * Derive the checklist enum stance from mean sentiment. Returns null
 * when there are no observations (`count === 0`).
 */
export function inferOverallStance(
  meanSentiment: number,
  count: number,
): CommunityFeedbackStance | null {
  if (count <= 0 || !Number.isFinite(meanSentiment)) return null;
  if (meanSentiment <= PSE_OPPOSITIONAL_THRESHOLD) return 'oppositional';
  if (meanSentiment >= PSE_SUPPORTIVE_THRESHOLD) return 'supportive';
  if (Math.abs(meanSentiment) <= PSE_NEUTRAL_BAND) return 'neutral';
  return 'mixed';
}

/** Read the general-datacenter bucket from a perTech array. */
export function getGeneralDatacenterBucket(
  perTech: readonly PerTechSentiment[] | undefined,
): PerTechSentiment | undefined {
  return perTech?.find(b => b.btmTech === 'general-datacenter');
}

function mapSourceId(
  sourceId: PublicSentimentEntry['sourceId'],
): PsrPublicSentimentEntry['source'] {
  switch (sourceId) {
    case 'gdelt':
      return 'gdelt';
    case 'local-minutes':
      return 'municipal-minutes';
    case 'federal-dockets':
      return 'regulations-gov';
    default:
      return 'manual';
  }
}

/** Adapt SED entries to the PSR theme extractor input shape. */
export function publicSentimentEntriesToPsr(
  entries: readonly PublicSentimentEntry[],
): PsrPublicSentimentEntry[] {
  return entries
    .filter(e => e && typeof e.supportingSnippet === 'string' && e.supportingSnippet.trim())
    .map(e => ({
      source: mapSourceId(e.sourceId),
      observedAt: e.fetchedAt,
      text: e.supportingSnippet,
      polarity: Number.isFinite(e.sentimentScore) ? e.sentimentScore : 0,
      // The trend helper derives its fixed BTM bucket from `theme` + `text`.
      // SED entries already carry the authoritative bucket, so preserve it
      // here instead of asking a keyword heuristic to rediscover it from a
      // snippet that may only say "the project".
      theme: e.btmTech,
      refUrl: e.url,
    }));
}

/**
 * Theme names for keyThemes — prefers `extractThemes()` (PSR-3 contract)
 * and falls back to taxonomy substring hits with a lower sample floor.
 */
export function extractKeyThemeNames(
  entries: readonly PublicSentimentEntry[] | undefined,
): string[] {
  if (!entries?.length) return [];

  const psrEntries = publicSentimentEntriesToPsr(entries);
  const fromExtractor = extractThemes(psrEntries).map(t => t.name);
  if (fromExtractor.length > 0) return fromExtractor;

  const matchesByTheme = new Map<string, number>();
  for (const row of THEME_TAXONOMY) {
    matchesByTheme.set(row.name, 0);
  }
  for (const e of entries) {
    const haystack = (e.supportingSnippet ?? '').toLowerCase();
    if (!haystack) continue;
    for (const row of THEME_TAXONOMY) {
      if (row.patterns.some(p => haystack.includes(p))) {
        matchesByTheme.set(row.name, (matchesByTheme.get(row.name) ?? 0) + 1);
      }
    }
  }

  return [...matchesByTheme.entries()]
    .filter(([, count]) => count >= COVERAGE_THEME_MIN_SAMPLE_COUNT)
    .sort((a, b) => b[1] - a[1])
    .slice(0, THEME_MAX_COUNT)
    .map(([name]) => name);
}

function buildPublicSentimentNotes(args: {
  section: PublicSentimentSection;
  bucket: PerTechSentiment;
  themeNames: string[];
}): string {
  const parts: string[] = [];
  parts.push(
    'Prefilled from public records (GDELT, council minutes, federal dockets) — upload stakeholder log to override.',
  );
  parts.push(
    `General data-center sentiment: ${args.bucket.meanSentiment.toFixed(2)} ` +
      `(${args.bucket.count} observation${args.bucket.count === 1 ? '' : 's'}).`,
  );
  if (args.section.sourcesAttempted.length > 0) {
    parts.push(`Sources: ${args.section.sourcesAttempted.join(', ')}.`);
  }
  if (args.themeNames.length > 0) {
    parts.push(`Dominant themes: ${args.themeNames.join(', ')}.`);
  }
  parts.push(
    'Stakeholder count and last engagement date require a site-specific engagement log — left blank.',
  );
  return parts.join(' ');
}

/**
 * Map a `PublicSentimentSection` onto the community-feedback coverage shape.
 */
export function mapPublicSentimentToCoverage(
  section: PublicSentimentSection,
): PublicSentimentCoverageFill | null {
  if (section.status === 'unavailable') return null;

  const bucket = getGeneralDatacenterBucket(section.perTech);
  if (!bucket || bucket.count <= 0) return null;

  const fields: PublicSentimentCoverageFill['fields'] = {};
  let filled = 0;

  const score = meanSentimentToScore0to100(bucket.meanSentiment);
  if (score != null) {
    fields.sentimentScore_0to100 = score;
    filled += 1;
  }

  const stance = inferOverallStance(bucket.meanSentiment, bucket.count);
  if (stance) {
    fields.overallStance = stance;
    filled += 1;
  }

  const themeNames = extractKeyThemeNames(section.entries);
  if (themeNames.length > 0) {
    fields.keyThemes = themeNames.join(', ');
    filled += 1;
  }

  if (filled === 0) return null;

  const confidence = inferAutoFillConfidence({
    fillSource: 'provider',
    fieldsFilledCount: filled,
    fieldsTotalCount: 3,
  });

  return {
    fields,
    notes: buildPublicSentimentNotes({ section, bucket, themeNames }),
    fieldsFilledCount: filled,
    fieldsTotalCount: COMMUNITY_FEEDBACK_FIELD_KEYS.length,
    sourceUrl: PUBLIC_SENTIMENT_SOURCE_URL,
    sourceProvider: 'public-sentiment-orchestrator',
    confidence,
  };
}

export function mapPublicSentimentSedToCoverage(
  sed: SiteEvidenceDocument,
): Pick<
  RequirementAutoFill,
  'fields' | 'notes' | 'confidence' | 'fieldsFilledCount' | 'fieldsTotalCount'
> {
  const section = sed.publicSentiment;
  if (!section) {
    return {
      fields: {},
      notes: 'No public sentiment on SED — run public sentiment prefetch or upload stakeholder log.',
      confidence: { tier: 'low', value: 0.45, rationale: 'No publicSentiment section' },
      fieldsFilledCount: 0,
      fieldsTotalCount: COMMUNITY_FEEDBACK_FIELD_KEYS.length,
    };
  }

  const mapped = mapPublicSentimentToCoverage(section);
  if (!mapped) {
    return {
      fields: {},
      notes: 'Public sentiment section present but no general-datacenter observations — upload stakeholder log.',
      confidence: { tier: 'low', value: 0.45, rationale: 'Empty general-datacenter bucket' },
      fieldsFilledCount: 0,
      fieldsTotalCount: COMMUNITY_FEEDBACK_FIELD_KEYS.length,
    };
  }

  return {
    fields: mapped.fields as Record<string, string | number>,
    notes: mapped.notes,
    confidence: mapped.confidence,
    fieldsFilledCount: mapped.fieldsFilledCount,
    fieldsTotalCount: mapped.fieldsTotalCount,
  };
}

export function publicSentimentSedToRequirementAutoFill(
  sed: SiteEvidenceDocument,
): RequirementAutoFill | null {
  const mapped = mapPublicSentimentSedToCoverage(sed);
  if (mapped.fieldsFilledCount === 0) return null;
  const fill = mapPublicSentimentToCoverage(sed.publicSentiment!);
  return {
    requirementId: 'community-feedback-summary',
    ...mapped,
    sourceProvider: fill?.sourceProvider ?? 'public-sentiment-orchestrator',
    sourceUrl: fill?.sourceUrl ?? PUBLIC_SENTIMENT_SOURCE_URL,
  };
}
